import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../api/client';

// ─── Color + Icon config per notification type ────────────────────────────────
const TYPE_COLORS = {
  reservation_due:  '#c8a96e',
  checkout_due:     '#e67e22',
  room_ready:       '#2ecc71',
  rollover:         '#3498db',
  system:           '#9b59b6',
  approval_request: '#e67e22',
  approval_done:    '#2ecc71',
  approval_rejected:'#e74c3c',
  low_stock:        '#e74c3c',
};

const TYPE_ICONS = {
  reservation_due:  '📅',
  checkout_due:     '🚪',
  room_ready:       '🛏️',
  rollover:         '🔄',
  system:           '⚙️',
  approval_request: '⏳',
  approval_done:    '✅',
  approval_rejected:'❌',
  low_stock:        '⚠️',
};

const TYPE_LABELS = {
  reservation_due:  'Reservation',
  checkout_due:     'Checkout',
  room_ready:       'Room Ready',
  rollover:         'Daily Rollover',
  system:           'System',
  approval_request: 'Approval Needed',
  approval_done:    'Approved',
  approval_rejected:'Rejected',
  low_stock:        'Low Stock',
};

const SUBTYPE_LABELS = {
  shopping_list: '🛒 Shopping List',
  complimentary: '🎁 Complimentary',
  leave:         '📋 Leave',
  spoilage:      '🗑️ Spoilage',
  void:          '🚫 Void',
  low_stock:     '⚠️ Stock',
};

// ─── ApprovalStamp SVG – matches the brand stamp images ──────────────────────
const STAMP_CFG = {
  storekeeper: { label:'STOREKEEPER', scaleFont: 0.85 },
  manager:     { label:'MANAGER',     scaleFont: 1    },
  accountant:  { label:'ACCOUNTANT',  scaleFont: 0.82 },
  director:    { label:'DIRECTOR',    scaleFont: 0.9  },
  hr:          { label:'HR',          scaleFont: 1.1  },
};

function ApprovalStamp({ role, by, at, size = 68 }) {
  const cfg  = STAMP_CFG[role] || { label: role.toUpperCase(), scaleFont: 1 };
  const cx   = size / 2;
  const cy   = size / 2;
  const rOuter = cx - 3;
  const rInner = cx - 10;
  const uid    = `stamp_${role}_${size}`;
  const mainFontSize = Math.round(9 * cfg.scaleFont * (size / 68));

  // Arc radius for text path
  const arcR = rOuter - 5;

  return (
    <div style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', gap:3 }}>
      <svg
        width={size} height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ display:'block', opacity:0.9 }}
      >
        <defs>
          {/* Top arc path for "GRAND MEMORIES" */}
          <path
            id={`${uid}_top`}
            d={`M ${cx - arcR},${cy} a ${arcR},${arcR} 0 1,1 ${arcR*2},0`}
          />
          {/* Bottom arc path for "RESORT & SPA" */}
          <path
            id={`${uid}_bot`}
            d={`M ${cx - arcR + 4},${cy} a ${arcR - 4},${arcR - 4} 0 0,0 ${(arcR-4)*2},0`}
          />
          <filter id={`${uid}_dist`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="3" result="n"/>
            <feDisplacementMap in="SourceGraphic" in2="n" scale="0.9"
              xChannelSelector="R" yChannelSelector="G"/>
          </filter>
        </defs>

        {/* Outer filled circle */}
        <circle cx={cx} cy={cy} r={rOuter}
          fill="#fdf6ec" stroke="#8B1A1A" strokeWidth="2.2"
          filter={`url(#${uid}_dist)`}/>

        {/* Inner ring */}
        <circle cx={cx} cy={cy} r={rInner}
          fill="none" stroke="#8B1A1A" strokeWidth="1.2"
          filter={`url(#${uid}_dist)`}/>

        {/* Top arc text: GRAND MEMORIES */}
        <text fontSize={Math.max(5, size * 0.077)}
          fill="#8B1A1A"
          fontFamily="'Arial Narrow',Arial,sans-serif"
          fontWeight="800" letterSpacing="1.4">
          <textPath href={`#${uid}_top`} startOffset="15%">
            GRAND MEMORIES
          </textPath>
        </text>

        {/* Separator lines */}
        <line
          x1={cx - rInner + 5} y1={cy - rInner * 0.40}
          x2={cx + rInner - 5} y2={cy - rInner * 0.40}
          stroke="#8B1A1A" strokeWidth="0.9"/>
        <line
          x1={cx - rInner + 5} y1={cy + rInner * 0.20}
          x2={cx + rInner - 5} y2={cy + rInner * 0.20}
          stroke="#8B1A1A" strokeWidth="0.9"/>

        {/* Main role label */}
        <text
          x={cx} y={cy + 1}
          textAnchor="middle" dominantBaseline="middle"
          fontSize={mainFontSize}
          fill="#8B1A1A"
          fontFamily="'Arial Narrow',Arial,sans-serif"
          fontWeight="900" letterSpacing="0.3"
          filter={`url(#${uid}_dist)`}>
          {cfg.label}
        </text>

        {/* APPROVED */}
        <text
          x={cx} y={cy + rInner * 0.38}
          textAnchor="middle" dominantBaseline="middle"
          fontSize={Math.max(4.5, size * 0.069)}
          fill="#8B1A1A"
          fontFamily="'Arial Narrow',Arial,sans-serif"
          fontWeight="600" letterSpacing="1.8">
          — APPROVED —
        </text>

        {/* Stars */}
        <text x={cx - rInner * 0.55} y={cy + rInner * 0.66}
          textAnchor="middle" fontSize={Math.max(5, size * 0.09)}
          fill="#8B1A1A">★</text>
        <text x={cx + rInner * 0.55} y={cy + rInner * 0.66}
          textAnchor="middle" fontSize={Math.max(5, size * 0.09)}
          fill="#8B1A1A">★</text>

        {/* Bottom arc text: RESORT & SPA */}
        <text
          x={cx} y={cy + rInner * 0.85}
          textAnchor="middle" dominantBaseline="middle"
          fontSize={Math.max(4, size * 0.069)}
          fill="#8B1A1A"
          fontFamily="'Arial Narrow',Arial,sans-serif"
          fontWeight="700" letterSpacing="1.6">
          RESORT &amp; SPA
        </text>
      </svg>

      {/* By / date caption */}
      {(by || at) && (
        <div style={{
          fontSize:9, color:'#666', textAlign:'center',
          maxWidth:size, lineHeight:1.3,
        }}>
          {by && <div>{by}</div>}
          {at  && <div>{new Date(at).toLocaleDateString('en-KE',{day:'2-digit',month:'short'})}</div>}
        </div>
      )}
    </div>
  );
}

// ─── Decide which stamps a notification should display ───────────────────────
function extractApprover(body, keyword) {
  if (!body) return undefined;
  // Match "Manager John Doe approved" or "Manager John has" etc.
  const re = new RegExp(`${keyword}[:\\s]+([\\w][\\w\\s]{1,40}?)(?:\\s+(?:approved|has|rejected)|[.,]|$)`, 'i');
  const m  = body.match(re);
  return m ? m[1].trim() : undefined;
}

function getStamps(n) {
  if (!n) return [];
  const { notif_type: t, notif_subtype: s, body = '', created_at } = n;

  // ── Shopping List: 2-step approval ─────────────────────────────────────────
  if (s === 'shopping_list') {
    if (t === 'approval_done') {
      // Final approval — both manager + accountant stamps side-by-side
      return [
        { role: 'manager',    by: extractApprover(body, 'Manager'),    at: created_at },
        { role: 'accountant', by: extractApprover(body, 'Accountant'), at: created_at },
      ];
    }
    if (t === 'approval_request') {
      // Step 1: awaiting manager; Step 2: manager done, awaiting accountant
      const awaitingAccountant = body.toLowerCase().includes('accountant');
      if (awaitingAccountant) {
        return [{ role: 'manager', by: extractApprover(body, 'Manager'), at: created_at }];
      }
      return []; // still pending — no stamp yet
    }
  }

  // ── Complimentary Bill ──────────────────────────────────────────────────────
  if (s === 'complimentary') {
    if (t === 'approval_done')
      return [{ role: 'manager', by: extractApprover(body, 'Manager'), at: created_at }];
    return [];
  }

  // ── Leave Request ───────────────────────────────────────────────────────────
  if (s === 'leave') {
    if (t === 'approval_done')
      return [{ role: 'hr', by: extractApprover(body, 'HR') || extractApprover(body, 'Manager'), at: created_at }];
    return [];
  }

  // ── Spoilage Report ─────────────────────────────────────────────────────────
  if (s === 'spoilage') {
    if (t === 'approval_done')
      return [{ role: 'accountant', by: extractApprover(body, 'Accountant'), at: created_at }];
    return [];
  }

  // ── Void (system notice, no stamp needed) ──────────────────────────────────
  return [];
}

// ─── Human-readable navigation label for a notification ───────────────────────
function getNavLabel(n) {
  const map = {
    store: 'Open Storeroom →',
    accounting: 'Open Accounting →',
    folio: 'Open Folios →',
    payroll: 'Open Payroll →',
    costing: 'Open Kitchen →',
    reservations: 'Open Reservations →',
    leave: 'Open Leave →',
    restaurant_manager: 'Open Restaurant →',
    general_ledger: 'Open Ledger →',
    maintenance: 'Open Maintenance →',
  };
  return map[n.related_tab] || '→ tap to open';
}

// ─── Main panel ───────────────────────────────────────────────────────────────
export default function NotificationsPanel({ open, onClose, role, onNavigate }) {
  const [notifications, setNotifications] = useState([]);
  const [alerts,        setAlerts]        = useState([]);
  const [tab,           setTab]           = useState('approvals');
  const [filter,        setFilter]        = useState('all');

  const load = useCallback(async () => {
    try {
      const [n, c] = await Promise.all([
        api.getNotifications('?unread=false'),
        api.checkNotifications(),
      ]);
      setNotifications(n.data.results || n.data);
      setAlerts(c.data.alerts || []);
    } catch(e) {}
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const markAllRead = async () => { await api.markAllRead(); load(); };

  const handleAlertClick = (a) => {
    if (a.related_tab && onNavigate) {
      onNavigate(a.related_tab);
      onClose();
    }
  };

  const handleClick = async (n) => {
    // Always mark read when clicked
    if (!n.is_read) {
      // Optimistic update first for instant feedback
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
      api.markNotifRead(n.id).catch(() => {});
    }
    // Navigate to related tab regardless of read state
    if (n.related_tab && onNavigate) {
      onNavigate(n.related_tab);
      onClose();
    }
  };

  if (!open) return null;

  const APPROVAL_TYPES = ['approval_request','approval_done','approval_rejected','low_stock'];
  const approvalNotifs = notifications.filter(n => APPROVAL_TYPES.includes(n.notif_type));
  const historyNotifs  = notifications.filter(n => !APPROVAL_TYPES.includes(n.notif_type));
  const unreadApprovals = approvalNotifs.filter(n => !n.is_read).length;
  const unreadHistory   = historyNotifs.filter(n => !n.is_read).length;
  const unreadTotal     = notifications.filter(n => !n.is_read).length;

  const filtered = filter === 'all'
    ? approvalNotifs
    : approvalNotifs.filter(n => n.notif_type === filter);

  const TABS = [
    { id:'approvals', label:'⏳ Approvals', badge: unreadApprovals },
    { id:'alerts',    label:'⚡ Alerts',    badge: alerts.length   },
    { id:'history',   label:'📋 History',   badge: unreadHistory   },
  ];

  return (
    <>
      <div onClick={onClose}
        style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:998 }}/>

      <div style={{
        position:'fixed', top:0, right:0, bottom:0,
        width:'min(420px,100vw)',
        background:'#0a0a0a',
        borderLeft:'1px solid #1c1c1c',
        zIndex:999,
        display:'flex', flexDirection:'column',
        boxShadow:'-6px 0 50px rgba(0,0,0,0.85)',
        fontFamily:"'Inter','Helvetica Neue',sans-serif",
      }}>

        {/* ── Header ── */}
        <div style={{
          padding:'14px 16px 10px',
          borderBottom:'1px solid #1c1c1c',
        }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={{ fontSize:10, fontWeight:800, color:'#c8a96e', letterSpacing:3 }}>
                NOTIFICATIONS
              </div>
              <div style={{ fontSize:11, color:'#333', marginTop:2, letterSpacing:1 }}>
                Grand Memories Resort &amp; Spa
              </div>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              {unreadTotal > 0 && (
                <button onClick={markAllRead}
                  style={{
                    background:'none', border:'1px solid #252525', borderRadius:4,
                    padding:'4px 10px', color:'#555', fontSize:11,
                    cursor:'pointer', fontFamily:'inherit',
                  }}>
                  Mark all read
                </button>
              )}
              <button onClick={onClose}
                style={{ background:'none', border:'none', color:'#555', fontSize:18,
                  cursor:'pointer', padding:'2px 4px' }}>
                ✕
              </button>
            </div>
          </div>

          {/* Summary banner */}
          {(unreadApprovals > 0 || alerts.length > 0) && (
            <div style={{
              marginTop:8, padding:'6px 10px',
              background:'rgba(200,169,110,0.07)',
              border:'1px solid rgba(200,169,110,0.18)',
              borderRadius:6,
              display:'flex', gap:14, flexWrap:'wrap',
              fontSize:11, color:'#c8a96e',
            }}>
              {unreadApprovals > 0 &&
                <span>⏳ {unreadApprovals} approval{unreadApprovals > 1 ? 's' : ''} need attention</span>}
              {alerts.length > 0 &&
                <span>⚡ {alerts.length} active alert{alerts.length > 1 ? 's' : ''}</span>}
            </div>
          )}
        </div>

        {/* ── Tabs ── */}
        <div style={{ display:'flex', borderBottom:'1px solid #161616' }}>
          {TABS.map(({ id, label, badge }) => (
            <button key={id} onClick={() => setTab(id)}
              style={{
                flex:1, padding:'10px 4px', background:'none', border:'none',
                borderBottom:`2px solid ${tab===id ? '#c8a96e' : 'transparent'}`,
                color: tab===id ? '#c8a96e' : '#484848',
                fontSize:11, cursor:'pointer', fontFamily:'inherit',
                fontWeight: tab===id ? 700 : 400,
              }}>
              {label}
              {badge > 0 && (
                <span style={{
                  marginLeft:5, fontSize:9, fontWeight:700,
                  background: id==='approvals' ? '#e67e22' : id==='alerts' ? '#e74c3c' : '#555',
                  color:'#fff', borderRadius:9, padding:'1px 5px',
                }}>
                  {badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Content ── */}
        <div style={{ flex:1, overflowY:'auto' }}>

          {/* APPROVALS */}
          {tab === 'approvals' && (
            <div>
              {/* Filter chips */}
              <div style={{
                padding:'8px 12px', borderBottom:'1px solid #141414',
                display:'flex', gap:5, flexWrap:'wrap',
              }}>
                {[
                  ['all','All'],
                  ['approval_request','⏳ Pending'],
                  ['approval_done','✅ Approved'],
                  ['approval_rejected','❌ Rejected'],
                  ['low_stock','⚠️ Stock'],
                ].map(([v,l]) => (
                  <button key={v} onClick={() => setFilter(v)}
                    style={{
                      padding:'3px 9px', fontSize:10, cursor:'pointer',
                      fontFamily:'inherit', fontWeight: filter===v ? 700 : 400,
                      borderRadius:11,
                      background: filter===v ? 'rgba(200,169,110,0.15)' : 'transparent',
                      border:`1px solid ${filter===v ? '#c8a96e' : '#1e1e1e'}`,
                      color: filter===v ? '#c8a96e' : '#484848',
                    }}>
                    {l}
                  </button>
                ))}
              </div>

              {filtered.length === 0 ? (
                <div style={{ textAlign:'center', padding:'55px 20px', color:'#2e2e2e' }}>
                  <div style={{ fontSize:32, marginBottom:10 }}>✓</div>
                  <div style={{ fontSize:13 }}>No approval notifications</div>
                </div>
              ) : filtered.map(n => {
                const stamps      = getStamps(n);
                const borderClr   = TYPE_COLORS[n.notif_type] || '#444';
                const canNavigate = !!n.related_tab;

                return (
                  <div key={n.id} onClick={() => handleClick(n)}
                    style={{
                      padding:'13px 15px', borderBottom:'1px solid #121212',
                      borderLeft:`3px solid ${n.is_read ? '#1e1e1e' : borderClr}`,
                      background: n.is_read ? 'transparent' : 'rgba(255,255,255,0.016)',
                      cursor: canNavigate ? 'pointer' : 'default',
                    }}>

                    <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                      {/* icon */}
                      <span style={{
                        fontSize:20, lineHeight:1, marginTop:1, flexShrink:0,
                        filter: n.is_read ? 'grayscale(0.7) opacity(0.5)' : 'none',
                      }}>
                        {TYPE_ICONS[n.notif_type] || '📌'}
                      </span>

                      <div style={{ flex:1, minWidth:0 }}>
                        {/* Pill row */}
                        <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginBottom:5 }}>
                          <span style={{
                            fontSize:9, fontWeight:700, letterSpacing:0.8,
                            textTransform:'uppercase',
                            color: n.is_read ? '#444' : borderClr,
                            border:`1px solid ${n.is_read ? '#282828' : borderClr}`,
                            borderRadius:10, padding:'1px 7px',
                          }}>
                            {TYPE_LABELS[n.notif_type] || 'Notice'}
                          </span>
                          {n.notif_subtype && SUBTYPE_LABELS[n.notif_subtype] && (
                            <span style={{
                              fontSize:9, color:'#555',
                              border:'1px solid #1e1e1e', borderRadius:10,
                              padding:'1px 7px',
                            }}>
                              {SUBTYPE_LABELS[n.notif_subtype]}
                            </span>
                          )}
                          {canNavigate && (
                            <span style={{
                              marginLeft:'auto', fontSize:9,
                              color: n.is_read ? '#3a3a3a' : '#2e7d50',
                              border:`1px solid ${n.is_read ? '#252525' : '#1a3d28'}`,
                              borderRadius:10, padding:'1px 7px',
                            }}>
                              {getNavLabel(n)}
                            </span>
                          )}
                        </div>

                        {/* Title */}
                        <div style={{
                          fontSize:13, lineHeight:1.4, marginBottom:4,
                          color: n.is_read ? '#555' : '#e0e0e0',
                          fontWeight: n.is_read ? 400 : 600,
                        }}>
                          {n.title}
                        </div>

                        {/* Body */}
                        {n.body && (
                          <div style={{
                            fontSize:11, lineHeight:1.5, marginBottom:6,
                            color: n.is_read ? '#3a3a3a' : '#666',
                          }}>
                            {n.body}
                          </div>
                        )}

                        {/* Stamps — shown when approved */}
                        {stamps.length > 0 && (
                          <div style={{
                            display:'flex', gap:stamps.length > 1 ? 16 : 10,
                            alignItems:'flex-start',
                            flexWrap:'wrap', marginTop:10,
                            padding:'12px 14px',
                            background:'rgba(200,169,110,0.04)',
                            border:'1px solid rgba(200,169,110,0.12)',
                            borderRadius:8,
                            position:'relative',
                          }}>
                            <div style={{
                              fontSize:9, color:'#444', width:'100%',
                              letterSpacing:1.5, textTransform:'uppercase', marginBottom:4,
                            }}>
                              Approval Stamp{stamps.length > 1 ? 's' : ''}
                            </div>
                            {stamps.map((s, i) => (
                              <React.Fragment key={i}>
                                <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                                  <ApprovalStamp
                                    role={s.role}
                                    by={s.by}
                                    at={s.at}
                                    size={stamps.length > 1 ? 72 : 80}
                                  />
                                </div>
                                {/* Divider between dual stamps */}
                                {i < stamps.length - 1 && (
                                  <div style={{
                                    width:1, background:'rgba(200,169,110,0.15)',
                                    alignSelf:'stretch', margin:'0 2px',
                                  }}/>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        )}

                        {/* Timestamp */}
                        <div style={{
                          display:'flex', justifyContent:'space-between', alignItems:'center',
                          marginTop:7,
                        }}>
                          <div style={{ fontSize:10, color:'#333' }}>
                            {new Date(n.created_at).toLocaleString('en-KE',{
                              day:'2-digit', month:'short',
                              hour:'2-digit', minute:'2-digit',
                            })}
                          </div>
                          {!n.is_read && (
                            <div style={{
                              width:7, height:7, borderRadius:'50%',
                              background: borderClr,
                            }}/>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ALERTS */}
          {tab === 'alerts' && (
            <div>
              {alerts.length === 0 ? (
                <div style={{ textAlign:'center', padding:'55px 20px', color:'#2e2e2e' }}>
                  <div style={{ fontSize:32, marginBottom:10 }}>✓</div>
                  <div style={{ fontSize:13 }}>No active alerts</div>
                </div>
              ) : alerts.map((a, i) => {
                const accentClr = a.days===0 ? '#e74c3c' : a.days===1 ? '#e67e22' : '#c8a96e';
                const canNav    = !!a.related_tab;
                return (
                  <div key={i}
                    onClick={() => handleAlertClick(a)}
                    style={{
                      padding:'14px 16px', borderBottom:'1px solid #121212',
                      borderLeft:`3px solid ${accentClr}`,
                      cursor: canNav ? 'pointer' : 'default',
                      background: 'rgba(255,255,255,0.012)',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { if (canNav) e.currentTarget.style.background='rgba(255,255,255,0.03)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.012)'; }}
                  >
                    <div style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                      <span style={{ fontSize:18, lineHeight:1, marginTop:2, flexShrink:0 }}>
                        {a.type === 'checkout_due' ? '🚪' : '📅'}
                      </span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, color:'#ccc', lineHeight:1.4 }}>
                          {a.message}
                        </div>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:5 }}>
                          <div style={{
                            fontSize:10, fontWeight:700,
                            textTransform:'uppercase', letterSpacing:1,
                            color: accentClr,
                          }}>
                            ● {a.days===0 ? 'Due Today' : a.days===1 ? 'Tomorrow' : 'In 2 Days'}
                          </div>
                          {canNav && (
                            <span style={{
                              fontSize:9, color:'#2e7d50',
                              border:'1px solid #1a3d28', borderRadius:10,
                              padding:'1px 7px',
                            }}>
                              → tap to open
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* HISTORY */}
          {tab === 'history' && (
            <div>
              {historyNotifs.length === 0 ? (
                <div style={{ textAlign:'center', padding:'55px 20px', color:'#2e2e2e' }}>
                  <div style={{ fontSize:13 }}>No notifications yet</div>
                </div>
              ) : historyNotifs.map(n => (
                <div key={n.id} onClick={() => handleClick(n)}
                  style={{
                    padding:'12px 15px', borderBottom:'1px solid #121212',
                    borderLeft:`3px solid ${n.is_read ? 'transparent' : TYPE_COLORS[n.notif_type]||'#555'}`,
                    cursor: (n.related_tab || !n.is_read) ? 'pointer' : 'default',
                    background: n.is_read ? 'transparent' : 'rgba(255,255,255,0.013)',
                  }}>
                  <div style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                    <span style={{
                      fontSize:14, marginTop:1,
                      filter: n.is_read ? 'grayscale(1) opacity(0.4)' : 'none',
                    }}>
                      {TYPE_ICONS[n.notif_type] || '📌'}
                    </span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{
                        fontSize:13, lineHeight:1.3,
                        color: n.is_read ? '#484848' : '#d0d0d0',
                        fontWeight: n.is_read ? 400 : 600,
                      }}>
                        {n.title}
                      </div>
                      {n.body && (
                        <div style={{ fontSize:11, color:'#3a3a3a', marginTop:3, lineHeight:1.4 }}>
                          {n.body.length > 90 ? n.body.slice(0,90) + '…' : n.body}
                        </div>
                      )}
                      <div style={{
                        display:'flex', justifyContent:'space-between',
                        alignItems:'center', marginTop:5,
                      }}>
                        <div style={{ fontSize:10, color:'#2e2e2e' }}>
                          {new Date(n.created_at).toLocaleString('en-KE',{
                            day:'2-digit', month:'short',
                            hour:'2-digit', minute:'2-digit',
                          })}
                        </div>
                        {!n.is_read && (
                          <div style={{
                            width:7, height:7, borderRadius:'50%',
                            background: TYPE_COLORS[n.notif_type] || '#c8a96e',
                          }}/>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding:'8px 16px', borderTop:'1px solid #141414',
          background:'#060606', fontSize:9, color:'#252525',
          textAlign:'center', letterSpacing:2, textTransform:'uppercase',
        }}>
          Grand Memories Resort &amp; Spa · Notification Centre
        </div>
      </div>
    </>
  );
}
